import { CartAddEvent } from '@theme/events';
import { formatMoney as formatThemeMoney } from '@theme/money-formatting';
import {
  CART_DRAWER_SECTION_ID,
  delayCartRequest,
  enqueueCartMutation,
  fetchCartWithTimeout,
  fetchCurrentCart as fetchAuthoritativeCart,
  getCachedCart,
  getCartRetryDelay,
  nextCartMutationSequence as getNextCartMutationSequence,
  noteCartMutationCooldown,
  storeLatestCart,
} from '@theme/sh-cart-service';

const PRODUCT_CACHE = new Map();
const PRODUCT_REQUESTS = new Map();
const CLOSE_ANIMATION_MS = 320;
const SUCCESS_CLOSE_DELAY_MS = 280;
const PRODUCT_REQUEST_TIMEOUT_MS = 10000;
const PRODUCT_REQUEST_RETRY_DELAY_MS = 650;
const PRODUCT_REQUEST_MAX_RETRY_DELAY_MS = 1500;
const PRODUCT_RETRYABLE_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504]);
const CART_REQUEST_TIMEOUT_MS = 15000;
const CART_SNAPSHOT_TIMEOUT_MS = 4000;
const CART_RECONCILE_POLL_DELAYS_MS = [500, 1200, 2200];
const CART_ADD_RETRYABLE_STATUS_CODES = new Set([408, 409, 425, 429, 500, 502, 503, 504]);
const CART_STATE_MANAGER_KEY = 'SecretHourCartState';

class SecretHourAddonQuickAdd {
  constructor() {
    this.root = null;
    this.drawer = null;
    this.closeButton = null;
    this.title = null;
    this.body = null;
    this.media = null;
    this.thumbnails = null;
    this.gallery = null;
    this.options = null;
    this.quantityTitle = null;
    this.quantityValue = null;
    this.quantityMinus = null;
    this.quantityPlus = null;
    this.details = null;
    this.detailsSummary = null;
    this.detailsContent = null;
    this.fullDetails = null;
    this.price = null;
    this.comparePrice = null;
    this.offer = null;
    this.submit = null;
    this.error = null;
    this.status = null;
    this.activeTrigger = null;
    this.returnFocusElement = null;
    this.closeTimer = null;
    this.closePromise = null;
    this.closeResolver = null;
    this.successTimer = null;
    this.scrollY = 0;
    this.isScrollLocked = false;
    this.openRequestId = 0;
    this.sessionId = 0;
    this.submissionId = 0;
    this.bodyTopBeforeLock = '';
    this.addAbortController = null;
    this.state = this.getBlankState();

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onRootClick = this.onRootClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onPageHide = this.onPageHide.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
  }

  init() {
    this.createRoot();
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener(CartAddEvent.eventName, this.onCartUpdate);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
  }

  getBlankState() {
    return {
      product: null,
      selectedOptions: [],
      selectedVariant: null,
      quantity: 1,
      offerText: '',
      submitLabel: 'ADD TO BAG',
      isSubmitting: false,
      isSuccess: false,
      copy: this.getDefaultCopy(),
      moneyFormat: '${{amount}}',
    };
  }

  getDefaultCopy() {
    return {
      close: 'Close',
      quantity: 'Quantity',
      decrease: 'Decrease quantity',
      increase: 'Increase quantity',
      details: 'Details',
      fullDetails: 'View more details',
      adding: 'Adding…',
      added: 'Added to cart',
      soldOut: 'Sold out',
      unavailable: 'Unavailable',
      error: 'Error adding to cart',
      slide: 'Slide [index] of [length]',
    };
  }

  createRoot() {
    const existingRoot = document.querySelector('[data-sh-addon-quick-add-root]');
    const hasCurrentMarkup = existingRoot instanceof HTMLDialogElement && existingRoot.querySelector('.sh-quick-add-media');
    if (hasCurrentMarkup) {
      this.root = existingRoot;
    } else {
      existingRoot?.remove();
      this.root = document.createElement('dialog');
      this.root.id = 'SHQuickAddDialog';
      this.root.className = 'sh-quick-add-root';
      this.root.setAttribute('data-sh-addon-quick-add-root', '');
      this.root.setAttribute('role', 'dialog');
      this.root.setAttribute('aria-modal', 'true');
      this.root.setAttribute('aria-labelledby', 'SHQuickAddTitle');
      this.root.innerHTML = `
        <div class="sh-quick-add-overlay" data-sh-quick-add-close aria-hidden="true"></div>
        <section class="sh-quick-add-drawer" role="document" tabindex="-1">
          <header class="sh-quick-add-header">
            <h2 class="sh-quick-add-title" id="SHQuickAddTitle"></h2>
            <button class="sh-quick-add-close" type="button" data-sh-quick-add-close></button>
          </header>
          <div class="sh-quick-add-body">
            <div class="sh-quick-add-layout">
              <div class="sh-quick-add-media">
                <div class="sh-quick-add-thumbnails" role="group"></div>
                <div class="sh-quick-add-gallery" tabindex="0"></div>
              </div>
              <div class="sh-quick-add-controls">
                <div class="sh-quick-add-options"></div>
                <div class="sh-quick-add-quantity">
                  <p class="sh-quick-add-quantity-title"></p>
                  <div class="sh-quick-add-quantity-control" role="group">
                    <button class="sh-quick-add-quantity-button" type="button" data-sh-quantity-minus>−</button>
                    <span class="sh-quick-add-quantity-value" aria-live="polite">1</span>
                    <button class="sh-quick-add-quantity-button" type="button" data-sh-quantity-plus>+</button>
                  </div>
                </div>
                <details class="sh-quick-add-details">
                  <summary class="sh-quick-add-details-summary"></summary>
                  <div class="sh-quick-add-details-content"></div>
                </details>
                <a class="sh-quick-add-full-details" href="#"></a>
                <p class="sh-quick-add-status" role="status" aria-live="polite"></p>
              </div>
            </div>
          </div>
          <footer class="sh-quick-add-footer">
            <p class="sh-quick-add-error" role="alert" aria-live="assertive"></p>
            <div class="sh-quick-add-price">
              <div class="sh-quick-add-price-row">
                <span class="sh-quick-add-current-price"></span>
                <s class="sh-quick-add-compare-price"></s>
              </div>
              <span class="sh-quick-add-offer"></span>
            </div>
            <button class="sh-quick-add-submit" type="button"></button>
          </footer>
        </section>
      `;
      document.body.appendChild(this.root);
    }

    this.drawer = this.root.querySelector('.sh-quick-add-drawer');
    this.closeButton = this.root.querySelector('.sh-quick-add-close');
    this.title = this.root.querySelector('.sh-quick-add-title');
    this.body = this.root.querySelector('.sh-quick-add-body');
    this.media = this.root.querySelector('.sh-quick-add-media');
    this.thumbnails = this.root.querySelector('.sh-quick-add-thumbnails');
    this.gallery = this.root.querySelector('.sh-quick-add-gallery');
    this.options = this.root.querySelector('.sh-quick-add-options');
    this.quantityTitle = this.root.querySelector('.sh-quick-add-quantity-title');
    this.quantityValue = this.root.querySelector('.sh-quick-add-quantity-value');
    this.quantityMinus = this.root.querySelector('[data-sh-quantity-minus]');
    this.quantityPlus = this.root.querySelector('[data-sh-quantity-plus]');
    this.details = this.root.querySelector('.sh-quick-add-details');
    this.detailsSummary = this.root.querySelector('.sh-quick-add-details-summary');
    this.detailsContent = this.root.querySelector('.sh-quick-add-details-content');
    this.fullDetails = this.root.querySelector('.sh-quick-add-full-details');
    this.price = this.root.querySelector('.sh-quick-add-current-price');
    this.comparePrice = this.root.querySelector('.sh-quick-add-compare-price');
    this.offer = this.root.querySelector('.sh-quick-add-offer');
    this.submit = this.root.querySelector('.sh-quick-add-submit');
    this.error = this.root.querySelector('.sh-quick-add-error');
    this.status = this.root.querySelector('.sh-quick-add-status');
    const footer = this.root.querySelector('.sh-quick-add-footer');
    if (this.error && footer && this.error.parentElement !== footer) footer.prepend(this.error);
    this.root.addEventListener('click', this.onRootClick);
    this.root.addEventListener('cancel', this.onCancel);
  }

  async onDocumentClick(event) {
    const trigger = event.target?.closest?.('[data-sh-addon-quick-add]');
    if (!(trigger instanceof HTMLButtonElement) || trigger.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    if (trigger.getAttribute('aria-busy') === 'true') return;

    if (this.addAbortController) {
      const copy = this.getCopy(trigger.closest('[data-sh-addon-quick-add-section]'));
      this.setTriggerError(trigger, copy.adding);
      return;
    }

    const requestId = ++this.openRequestId;
    this.clearTriggerStatus(trigger);
    trigger.setAttribute('aria-busy', 'true');

    try {
      const section = trigger.closest('[data-sh-addon-quick-add-section]');
      const product = await this.getProduct(
        trigger.dataset.productHandle || '',
        section?.dataset.shQuickAddRootUrl || '/',
        trigger
      );
      if (requestId !== this.openRequestId) return;
      if (!product.variants.length) throw new Error('This item has no purchasable variants.');
      this.open(product, trigger);
    } catch (error) {
      if (requestId !== this.openRequestId) return;
      this.setTriggerError(trigger, this.getSafeErrorMessage(error, true));
    } finally {
      trigger.removeAttribute('aria-busy');
    }
  }

  onRootClick(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.closest('[data-sh-quick-add-close]')) {
      this.close();
      return;
    }

    const thumbnail = target.closest('[data-sh-gallery-index]');
    if (thumbnail instanceof HTMLButtonElement) {
      this.selectGalleryImage(Number(thumbnail.dataset.shGalleryIndex));
      return;
    }

    const option = target.closest('[data-sh-option-index]');
    if (option instanceof HTMLButtonElement && !option.disabled) {
      this.selectOption(Number(option.dataset.shOptionIndex), option.dataset.shOptionValue || '');
      return;
    }

    if (target.closest('[data-sh-quantity-minus]')) {
      this.setQuantity(this.state.quantity - 1);
      return;
    }

    if (target.closest('[data-sh-quantity-plus]')) {
      this.setQuantity(this.state.quantity + 1);
      return;
    }

    if (target.closest('.sh-quick-add-submit')) {
      this.addToCart();
    }
  }

  onCancel(event) {
    event.preventDefault();
    this.close();
  }

  onPageHide() {
    this.closeImmediately();
  }

  onPageShow(event) {
    if (event.persisted) this.closeImmediately();
  }

  onKeyDown(event) {
    if (!this.root?.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab' || !this.drawer) return;
    const focusable = this.getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      this.drawer.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!this.drawer.contains(document.activeElement)) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  async getProduct(handle, rootUrl, trigger = null) {
    const cacheKey = String(handle || '').trim().toLowerCase();
    if (!cacheKey) throw new Error('Missing product handle.');
    if (PRODUCT_CACHE.has(cacheKey)) return PRODUCT_CACHE.get(cacheKey);

    const embeddedProduct = this.getEmbeddedProduct(trigger, cacheKey);
    if (embeddedProduct) {
      PRODUCT_CACHE.set(cacheKey, embeddedProduct);
      return embeddedProduct;
    }

    if (PRODUCT_REQUESTS.has(cacheKey)) return PRODUCT_REQUESTS.get(cacheKey);

    const request = this.fetchProduct(cacheKey, rootUrl)
      .then((product) => {
        const validatedProduct = this.validateProduct(product);
        PRODUCT_CACHE.set(cacheKey, validatedProduct);
        return validatedProduct;
      })
      .finally(() => {
        if (PRODUCT_REQUESTS.get(cacheKey) === request) PRODUCT_REQUESTS.delete(cacheKey);
      });

    PRODUCT_REQUESTS.set(cacheKey, request);
    return request;
  }

  getEmbeddedProduct(trigger, handle) {
    const card = trigger?.closest?.('.sh-adore-rec-card');
    const payload = card?.querySelector?.('[data-sh-quick-add-product]');
    if (!(payload instanceof HTMLScriptElement) || payload.dataset.invalid === 'true') return null;

    try {
      const rawProduct = JSON.parse(payload.textContent || '');
      return this.validateProduct(this.normalizeProduct(rawProduct, handle));
    } catch (_) {
      payload.dataset.invalid = 'true';
      return null;
    }
  }

  async fetchProduct(handle, rootUrl) {
    const root = String(rootUrl || '/').replace(/\/?$/, '/');
    const url = `${root}products/${encodeURIComponent(handle)}.js`;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await this.fetchWithTimeout(url, {
          cache: attempt === 0 ? 'force-cache' : 'reload',
          headers: {
            Accept: 'application/json',
          },
        }, PRODUCT_REQUEST_TIMEOUT_MS);
        const rawProduct = await this.parseProductResponse(response);
        return this.validateProduct(this.normalizeProduct(rawProduct, handle));
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        const canRetry = attempt === 0 && this.isRetryableProductError(error);
        if (canRetry) {
          await this.delay(this.getProductRetryDelay(error));
          continue;
        }
        throw new Error('Unable to load this item. Please try again.');
      }
    }

    throw new Error('Unable to load this item. Please try again.');
  }

  async parseProductResponse(response) {
    const text = await response.text();
    const isVerificationHtml = /<!doctype|<html|local_rate_limited|verifying your connection/i.test(text);
    let rawProduct;

    try {
      rawProduct = JSON.parse(text);
    } catch (_) {
      const error = new Error('Unable to load this item. Please try again.');
      error.status = isVerificationHtml ? 429 : response.status;
      error.isTransient = isVerificationHtml || response.ok || PRODUCT_RETRYABLE_STATUS_CODES.has(response.status);
      error.retryAfterMs = this.getProductRetryAfterMs(response);
      throw error;
    }

    const payloadStatus = Number(rawProduct?.status);
    if (!response.ok || (Number.isFinite(payloadStatus) && payloadStatus >= 400)) {
      const status = Number.isFinite(payloadStatus) && payloadStatus >= 400 ? payloadStatus : response.status;
      const error = new Error('Unable to load this item. Please try again.');
      error.status = status;
      error.isTransient = PRODUCT_RETRYABLE_STATUS_CODES.has(status);
      error.retryAfterMs = this.getProductRetryAfterMs(response);
      throw error;
    }

    return rawProduct;
  }

  isRetryableProductError(error) {
    if (error instanceof TypeError || error?.name === 'TimeoutError') return true;
    return error?.isTransient === true || PRODUCT_RETRYABLE_STATUS_CODES.has(Number(error?.status));
  }

  getProductRetryAfterMs(response) {
    const retryAfterSeconds = Number(response.headers?.get?.('Retry-After'));
    if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) return 0;
    return retryAfterSeconds * 1000;
  }

  getProductRetryDelay(error) {
    const retryAfterMs = Number(error?.retryAfterMs);
    if (Number.isFinite(retryAfterMs) && retryAfterMs > 0) {
      return Math.min(retryAfterMs, PRODUCT_REQUEST_MAX_RETRY_DELAY_MS);
    }
    return PRODUCT_REQUEST_RETRY_DELAY_MS;
  }

  validateProduct(product) {
    if (!product?.id || !product?.handle || !Array.isArray(product.variants) || !product.variants.length) {
      throw new Error('Unable to load this item. Please try again.');
    }
    return product;
  }

  normalizeProduct(rawProduct, fallbackHandle) {
    const handle = String(rawProduct?.handle || fallbackHandle || '').trim();
    const variants = (rawProduct.variants || []).map((variant) => ({
      ...variant,
      id: String(variant.id),
      options: this.getVariantOptions(variant),
      available: variant.available !== false,
      price: Number(variant.price ?? rawProduct.price ?? 0),
      compare_at_price: Number(variant.compare_at_price || 0),
      image: this.getVariantImage(variant),
    }));

    const images = this.getProductImages(rawProduct, variants);
    return {
      id: String(rawProduct.id || ''),
      handle,
      title: String(rawProduct.title || ''),
      url: rawProduct.url || (handle ? `/products/${handle}` : '#'),
      description: String(rawProduct.description || ''),
      vendor: String(rawProduct.vendor || ''),
      type: String(rawProduct.type || ''),
      featured_image: this.normalizeImage(rawProduct.featured_image) || images[0] || '',
      images,
      options: this.getProductOptions(rawProduct, variants),
      variants,
    };
  }

  getVariantOptions(variant) {
    if (Array.isArray(variant.options) && variant.options.length) {
      return variant.options.map((value) => String(value));
    }

    return [variant.option1, variant.option2, variant.option3]
      .filter((value) => value !== null && value !== undefined && value !== '')
      .map((value) => String(value));
  }

  getProductOptions(product, variants) {
    const sourceOptions = Array.isArray(product.options_with_values) && product.options_with_values.length
      ? product.options_with_values
      : product.options;

    if (Array.isArray(sourceOptions) && sourceOptions.length) {
      return sourceOptions.map((option, index) => {
        if (typeof option === 'string') {
          return {
            name: option,
            values: this.uniqueValues(variants.map((variant) => variant.options[index]).filter(Boolean)),
          };
        }

        return {
          name: option.name || option.title || `Option ${index + 1}`,
          values: Array.isArray(option.values)
            ? option.values.map((value) => String(value))
            : this.uniqueValues(variants.map((variant) => variant.options[index]).filter(Boolean)),
        };
      });
    }

    const longestOptions = variants.reduce((max, variant) => Math.max(max, variant.options.length), 0);
    return Array.from({ length: longestOptions }, (_, index) => ({
      name: `Option ${index + 1}`,
      values: this.uniqueValues(variants.map((variant) => variant.options[index]).filter(Boolean)),
    }));
  }

  getProductImages(product, variants) {
    const images = [];
    const addImage = (image) => {
      const normalized = this.normalizeImage(image);
      if (normalized && !images.some((item) => this.isSameImage(item, normalized))) images.push(normalized);
    };

    addImage(product.featured_image);
    if (Array.isArray(product.images)) product.images.forEach(addImage);
    if (Array.isArray(product.media)) product.media.forEach(addImage);
    variants.forEach((variant) => addImage(variant.image));
    return images;
  }

  normalizeImage(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    if (typeof image.src === 'string') return image.src;
    if (typeof image.url === 'string') return image.url;
    if (typeof image.preview_image === 'string') return image.preview_image;
    if (image.preview_image?.src) return image.preview_image.src;
    if (image.featured_image?.src) return image.featured_image.src;
    return '';
  }

  getVariantImage(variant) {
    return (
      this.normalizeImage(variant.featured_image) ||
      this.normalizeImage(variant.featured_media) ||
      this.normalizeImage(variant.image)
    );
  }

  uniqueValues(values) {
    return [...new Set(values.map((value) => String(value)))];
  }

  open(product, trigger) {
    window.clearTimeout(this.successTimer);
    ++this.sessionId;

    const firstVariant = product.variants.find((variant) => variant.available) || product.variants[0] || null;
    const section = trigger.closest('[data-sh-addon-quick-add-section]');
    this.state = {
      product,
      selectedOptions: firstVariant?.options ? [...firstVariant.options] : [],
      selectedVariant: firstVariant,
      quantity: 1,
      offerText: trigger.dataset.offerText || '',
      submitLabel: section?.dataset.shQuickAddSubmitLabel || 'ADD TO BAG',
      isSubmitting: false,
      isSuccess: false,
      copy: this.getCopy(section),
      moneyFormat: section?.dataset.shQuickAddMoneyFormat || '${{amount}}',
    };
    this.activeTrigger = trigger;
    this.returnFocusElement = trigger;

    this.applyCopy();
    this.render();
    this.lockScroll();
    if (!this.root.open) this.root.showModal();

    requestAnimationFrame(() => {
      this.root.classList.add('is-open');
      this.closeButton?.focus({ preventScroll: true });
    });
  }

  close() {
    if (!this.root?.open) return Promise.resolve();
    if (this.root.classList.contains('is-closing')) return this.closePromise || Promise.resolve();

    ++this.openRequestId;
    ++this.sessionId;
    window.clearTimeout(this.successTimer);
    this.root.classList.remove('is-open');
    this.root.classList.add('is-closing');
    const returnFocusElement = this.returnFocusElement;
    const closeDelay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : CLOSE_ANIMATION_MS;

    window.clearTimeout(this.closeTimer);
    this.closePromise = new Promise((resolve) => {
      this.closeResolver = resolve;
      this.closeTimer = window.setTimeout(() => {
        if (this.root.open) this.root.close();
        this.root.classList.remove('is-closing');
        this.unlockScroll();
        this.state = this.getBlankState();
        this.activeTrigger = null;
        this.returnFocusElement = null;
        if (returnFocusElement?.isConnected) returnFocusElement.focus({ preventScroll: true });
        this.closePromise = null;
        this.closeResolver = null;
        resolve();
      }, closeDelay);
    });
    return this.closePromise;
  }

  closeImmediately() {
    const resolveClose = this.closeResolver;
    ++this.openRequestId;
    ++this.sessionId;
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.successTimer);
    if (this.root?.open) this.root.close();
    this.root?.classList.remove('is-open', 'is-closing');
    this.unlockScroll();
    this.state = this.getBlankState();
    this.activeTrigger = null;
    this.returnFocusElement = null;
    this.closePromise = null;
    this.closeResolver = null;
    resolveClose?.();
  }

  isOpen() {
    return Boolean(this.root?.open && this.root.classList.contains('is-open'));
  }

  getCopy(section) {
    const defaults = this.getDefaultCopy();
    const data = section?.dataset || {};
    return {
      close: data.shQuickAddCloseLabel || defaults.close,
      quantity: data.shQuickAddQuantityLabel || defaults.quantity,
      decrease: data.shQuickAddDecreaseLabel || defaults.decrease,
      increase: data.shQuickAddIncreaseLabel || defaults.increase,
      details: data.shQuickAddDetailsLabel || defaults.details,
      fullDetails: data.shQuickAddFullDetailsLabel || defaults.fullDetails,
      adding: data.shQuickAddAddingLabel || defaults.adding,
      added: data.shQuickAddAddedLabel || defaults.added,
      soldOut: data.shQuickAddSoldOutLabel || defaults.soldOut,
      unavailable: data.shQuickAddUnavailableLabel || defaults.unavailable,
      error: data.shQuickAddErrorLabel || defaults.error,
      slide: data.shQuickAddSlideLabel || defaults.slide,
    };
  }

  applyCopy() {
    const { copy, product } = this.state;
    this.closeButton.setAttribute('aria-label', `${copy.close}: ${product.title}`);
    this.quantityTitle.textContent = copy.quantity;
    this.quantityMinus.setAttribute('aria-label', copy.decrease);
    this.quantityPlus.setAttribute('aria-label', copy.increase);
    this.root.querySelector('.sh-quick-add-quantity-control')?.setAttribute('aria-label', copy.quantity);
    this.detailsSummary.textContent = copy.details;
    this.fullDetails.textContent = copy.fullDetails;
    this.gallery.setAttribute('aria-label', `${product.title} — ${copy.slide.replace('[index]', '1').replace('[length]', String(product.images.length || 1))}`);
    this.thumbnails.setAttribute('aria-label', product.title);
  }

  render() {
    if (!this.state.product) return;
    this.title.textContent = this.state.product.title;
    this.body?.scrollTo({ top: 0, behavior: 'auto' });
    this.renderGallery();
    this.renderOptions();
    this.renderDetails();
    this.updateQuantity();
    this.updateVariantUi();
    this.clearError();
    this.setStatus('');
  }

  renderGallery() {
    const images = this.getDisplayImages().slice(0, 8);
    this.gallery.scrollLeft = 0;
    this.gallery.replaceChildren();
    this.thumbnails.replaceChildren();

    images.forEach((src, index) => {
      const slide = document.createElement('div');
      slide.className = 'sh-quick-add-gallery__slide';
      slide.dataset.shImage = src;
      slide.setAttribute('role', 'group');
      const slideLabel = this.state.copy.slide
        .replace('[index]', String(index + 1))
        .replace('[length]', String(images.length));
      slide.setAttribute('aria-label', slideLabel);
      slide.appendChild(this.createImage(src, `${this.state.product.title} — ${slideLabel}`, index));
      this.gallery.appendChild(slide);

      const thumbnail = document.createElement('button');
      thumbnail.type = 'button';
      thumbnail.className = 'sh-quick-add-thumbnail';
      thumbnail.dataset.shGalleryIndex = String(index);
      thumbnail.setAttribute('aria-label', `${this.state.product.title} — ${slideLabel}`);
      thumbnail.setAttribute('aria-pressed', String(index === 0));
      if (index === 0) {
        thumbnail.classList.add('is-active');
        thumbnail.setAttribute('aria-current', 'true');
      }
      thumbnail.appendChild(this.createThumbnailImage(src));
      this.thumbnails.appendChild(thumbnail);
    });

    this.media.hidden = !images.length;
    this.media.classList.toggle('has-single-image', images.length < 2);
    this.thumbnails.hidden = images.length < 2;
    this.gallery.hidden = !images.length;
    this.gallery.scrollLeft = 0;
    this.setActiveGalleryThumbnail(0);
  }

  selectGalleryImage(index) {
    const slides = [...this.gallery.querySelectorAll('.sh-quick-add-gallery__slide')];
    const firstSlide = slides[0];
    const slide = slides[index];
    if (!firstSlide || !slide) return;

    this.setActiveGalleryThumbnail(index);
    this.gallery.scrollLeft = slide.offsetLeft - firstSlide.offsetLeft;
  }

  setActiveGalleryThumbnail(activeIndex) {
    this.thumbnails.querySelectorAll('[data-sh-gallery-index]').forEach((thumbnail, index) => {
      const isActive = index === activeIndex;
      thumbnail.classList.toggle('is-active', isActive);
      thumbnail.setAttribute('aria-pressed', String(isActive));
      if (isActive) {
        thumbnail.setAttribute('aria-current', 'true');
      } else {
        thumbnail.removeAttribute('aria-current');
      }
    });
  }

  getDisplayImages() {
    const images = [...(this.state.product?.images || [])];
    const variantImage = this.state.selectedVariant?.image;
    if (variantImage) {
      const matchingIndex = images.findIndex((image) => this.isSameImage(image, variantImage));
      if (matchingIndex >= 0) images.splice(matchingIndex, 1);
      images.unshift(variantImage);
    }
    if (!images.length && this.state.product?.featured_image) images.push(this.state.product.featured_image);
    return images;
  }

  createImage(src, alt, index) {
    const image = document.createElement('img');
    image.alt = alt || '';
    image.width = 720;
    image.height = 900;
    image.decoding = 'async';
    image.loading = index === 0 ? 'eager' : 'lazy';
    image.fetchPriority = index === 0 ? 'high' : 'low';
    image.sizes = '(max-width: 767px) 78vw, 340px';
    image.src = this.getSizedImageUrl(src, 720);
    image.srcset = [320, 480, 640, 720, 900]
      .map((width) => `${this.getSizedImageUrl(src, width)} ${width}w`)
      .join(', ');
    return image;
  }

  createThumbnailImage(src) {
    const image = document.createElement('img');
    image.alt = '';
    image.width = 96;
    image.height = 120;
    image.decoding = 'async';
    image.loading = window.matchMedia('(min-width: 768px)').matches ? 'eager' : 'lazy';
    image.fetchPriority = 'low';
    image.sizes = '48px';
    image.src = this.getSizedImageUrl(src, 120);
    image.srcset = [64, 96, 120]
      .map((width) => `${this.getSizedImageUrl(src, width)} ${width}w`)
      .join(', ');
    return image;
  }

  getSizedImageUrl(src, width) {
    if (!src) return '';

    try {
      const normalizedSrc = src.startsWith('//') ? `https:${src}` : src;
      const url = new URL(normalizedSrc, window.location.origin);
      if (url.hostname.includes('shopify.com') || url.hostname.includes('shopifycdn.com')) {
        url.searchParams.set('width', String(width));
      }
      return url.toString();
    } catch (_) {
      return src;
    }
  }

  isSameImage(first, second) {
    const normalize = (src) => String(src || '').replace(/^https?:/, '').replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return normalize(first) === normalize(second);
  }

  renderOptions() {
    this.options.replaceChildren();
    const visibleOptions = this.getVisibleOptions();
    this.options.hidden = !visibleOptions.length;

    visibleOptions.forEach(({ option, originalIndex }) => {
      const group = document.createElement('fieldset');
      group.className = 'sh-quick-add-option-group';

      const heading = document.createElement('legend');
      heading.className = 'sh-quick-add-option-heading';
      heading.textContent = option.name;

      const list = document.createElement('div');
      list.className = 'sh-quick-add-option-list';

      option.values.forEach((value) => {
        const status = this.getOptionValueStatus(originalIndex, value);
        const isSelected = this.state.selectedOptions[originalIndex] === value;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sh-quick-add-option';
        button.dataset.shOptionIndex = String(originalIndex);
        button.dataset.shOptionValue = value;
        button.setAttribute('aria-pressed', String(isSelected));

        const colorImage = this.isColorOption(option.name)
          ? this.getOptionImage(originalIndex, value)
          : '';
        if (colorImage) {
          button.classList.add('sh-quick-add-option--color');
          const swatch = document.createElement('span');
          swatch.className = 'sh-quick-add-swatch';
          swatch.setAttribute('aria-hidden', 'true');
          swatch.style.backgroundImage = `url("${this.getSizedImageUrl(colorImage, 96).replace(/"/g, '\\"')}")`;
          const label = document.createElement('span');
          label.textContent = value;
          button.append(swatch, label);
        } else {
          button.textContent = value;
          button.classList.add(this.isSquareOption(option.name, value) ? 'sh-quick-add-option--square' : 'sh-quick-add-option--pill');
        }

        if (isSelected) button.classList.add('is-selected');
        if (status !== 'available') {
          const statusLabel = status === 'sold-out' ? this.state.copy.soldOut : this.state.copy.unavailable;
          button.disabled = true;
          button.classList.add('sh-quick-add-option-disabled');
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('aria-label', `${value} — ${statusLabel}`);
        } else {
          button.setAttribute('aria-label', value);
        }

        list.appendChild(button);
      });

      group.append(heading, list);
      this.options.appendChild(group);
    });
  }

  getVisibleOptions() {
    const product = this.state.product;
    if (!product?.options?.length) return [];

    const isSingleDefaultVariant =
      product.variants.length === 1 &&
      (product.variants[0].title === 'Default Title' || product.variants[0].options[0] === 'Default Title');

    return product.options
      .map((option, originalIndex) => ({ option, originalIndex }))
      .filter(({ option }) => {
        if (isSingleDefaultVariant) return false;
        if (option.values.length !== 1) return true;
        return option.name !== 'Title' && option.values[0] !== 'Default Title';
      });
  }

  isColorOption(name) {
    return /color|colour|颜色/i.test(name || '');
  }

  isSquareOption(name, value) {
    const compactRole = /size|band|cup|尺寸|罩杯/i.test(name || '');
    return compactRole && String(value).length <= 4 && !/\s/.test(String(value));
  }

  getOptionImage(optionIndex, value) {
    const previousSelections = this.state.selectedOptions.slice(0, optionIndex);
    const contextualVariant = this.state.product.variants.find((variant) => {
      return variant.image && variant.options[optionIndex] === value && previousSelections.every((selected, index) => !selected || variant.options[index] === selected);
    });
    return contextualVariant?.image || this.state.product.variants.find((variant) => variant.image && variant.options[optionIndex] === value)?.image || '';
  }

  getOptionValueStatus(index, value) {
    const matching = this.state.product.variants.filter((variant) => {
      if (variant.options[index] !== value) return false;
      return this.state.selectedOptions.slice(0, index).every((selectedValue, selectedIndex) => {
        return !selectedValue || variant.options[selectedIndex] === selectedValue;
      });
    });

    if (!matching.length) return 'unavailable';
    if (!matching.some((variant) => variant.available)) return 'sold-out';
    return 'available';
  }

  selectOption(index, value) {
    if (this.state.isSubmitting || this.state.isSuccess) return;
    this.state.selectedOptions[index] = value;

    let variant = this.findExactVariant(this.state.selectedOptions);
    if (!variant?.available) {
      const matchesThroughSelection = (item) => this.state.selectedOptions.slice(0, index + 1).every((selected, optionIndex) => {
        return !selected || item.options[optionIndex] === selected;
      });
      variant =
        this.state.product.variants.find((item) => item.available && matchesThroughSelection(item)) ||
        this.state.product.variants.find(matchesThroughSelection) ||
        variant;
      if (variant?.options) this.state.selectedOptions = [...variant.options];
    }

    this.state.selectedVariant = variant || null;
    this.renderOptions();
    this.renderGallery();
    this.updateQuantity();
    this.updateVariantUi();
    this.clearError();
    const focusTarget = [...this.options.querySelectorAll('[data-sh-option-index]')].find((button) => {
      return Number(button.dataset.shOptionIndex) === index && button.dataset.shOptionValue === value;
    });
    focusTarget?.focus({ preventScroll: true });
  }

  findExactVariant(selectedOptions) {
    return this.state.product.variants.find((variant) => {
      return variant.options.every((optionValue, index) => selectedOptions[index] === optionValue);
    });
  }

  setQuantity(quantity) {
    if (this.state.isSubmitting || this.state.isSuccess) return;
    const max = Number(this.state.selectedVariant?.quantity_rule?.max) || 99;
    this.state.quantity = Math.min(max, Math.max(1, Math.floor(Number(quantity) || 1)));
    this.updateQuantity();
  }

  updateQuantity() {
    const max = Number(this.state.selectedVariant?.quantity_rule?.max) || 99;
    this.quantityValue.textContent = String(this.state.quantity);
    this.quantityMinus.disabled = this.state.quantity <= 1 || this.state.isSubmitting || this.state.isSuccess;
    this.quantityPlus.disabled = this.state.quantity >= max || this.state.isSubmitting || this.state.isSuccess;
  }

  renderDetails() {
    const details = this.extractDetails(this.state.product.description);
    this.details.open = false;
    this.detailsContent.replaceChildren();

    if (details.summary) {
      const summary = document.createElement('p');
      summary.textContent = details.summary;
      this.detailsContent.appendChild(summary);
    }

    if (details.items.length) {
      const list = document.createElement('dl');
      details.items.forEach(({ label, value }) => {
        const term = document.createElement('dt');
        const description = document.createElement('dd');
        term.textContent = label;
        description.textContent = value;
        list.append(term, description);
      });
      this.detailsContent.appendChild(list);
    }

    this.details.hidden = !details.summary && !details.items.length;
  }

  extractDetails(descriptionHtml) {
    if (!descriptionHtml) return { summary: '', items: [] };
    const doc = new DOMParser().parseFromString(descriptionHtml, 'text/html');
    const paragraphSummary = [...doc.querySelectorAll('p')]
      .map((paragraph) => paragraph.textContent?.replace(/\s+/g, ' ').trim() || '')
      .find(Boolean) || '';
    const summarySource = paragraphSummary || doc.body.textContent?.replace(/\s+/g, ' ').trim() || '';
    const summary = this.truncateText(summarySource, 320);
    const items = [];

    doc.querySelectorAll('strong').forEach((strong) => {
      const label = (strong.textContent || '').replace(/\s*:\s*$/, '').trim();
      if (!/material|fabric|fit|care|package includes|包装|材质|面料|洗护|版型/i.test(label)) return;

      let value = '';
      let node = strong.nextSibling;
      while (node) {
        if (node.nodeName === 'BR' || (node instanceof HTMLElement && node.querySelector('strong'))) break;
        value += ` ${node.textContent || ''}`;
        node = node.nextSibling;
      }
      value = value.replace(/\s+/g, ' ').trim().replace(/^:\s*/, '');
      if (value && !items.some((item) => item.label.toLowerCase() === label.toLowerCase())) {
        items.push({ label, value: this.truncateText(value, 180) });
      }
    });

    return { summary, items: items.slice(0, 4) };
  }

  truncateText(value, maximumLength) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (text.length <= maximumLength) return text;
    const truncated = text.slice(0, maximumLength + 1).replace(/\s+\S*$/, '').trim();
    return `${truncated}…`;
  }

  updateVariantUi() {
    const variant = this.state.selectedVariant;
    const hasCompleteSelection = Boolean(
      variant && variant.options.every((value, index) => this.state.selectedOptions[index] === value)
    );
    const canSubmit = Boolean(variant?.id && variant.available && hasCompleteSelection);

    this.price.textContent = variant ? this.formatMoney(variant.price) : '';
    this.comparePrice.textContent = variant?.compare_at_price > variant?.price
      ? this.formatMoney(variant.compare_at_price)
      : '';
    this.comparePrice.hidden = !this.comparePrice.textContent;
    this.offer.textContent = this.state.offerText;
    this.offer.hidden = !this.state.offerText;
    this.updateFullDetailsLink();

    this.submit.disabled = !canSubmit || this.state.isSubmitting || this.state.isSuccess;
    if (this.state.isSuccess) {
      this.submit.textContent = this.state.copy.added;
    } else if (this.state.isSubmitting) {
      this.submit.textContent = this.state.copy.adding;
    } else if (!variant || !hasCompleteSelection) {
      this.submit.textContent = this.state.copy.unavailable;
    } else if (!variant.available) {
      this.submit.textContent = this.state.copy.soldOut;
    } else {
      this.submit.textContent = this.state.submitLabel;
    }
  }

  updateFullDetailsLink() {
    const { product, selectedVariant } = this.state;
    if (!product || !this.fullDetails) return;
    const url = new URL(product.url || `/products/${product.handle}`, window.location.origin);
    if (selectedVariant?.id) url.searchParams.set('variant', selectedVariant.id);
    this.fullDetails.href = `${url.pathname}${url.search}${url.hash}`;
  }

  formatMoney(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';

    try {
      return formatThemeMoney(
        amount,
        this.state.moneyFormat || '${{amount}}',
        window.Shopify?.currency?.active || 'USD'
      );
    } catch (_) {
      return `$${(amount / 100).toFixed(2)}`;
    }
  }

  async addToCart() {
    const variant = this.state.selectedVariant;
    if (!variant?.id || !variant.available || this.state.isSubmitting || this.state.isSuccess) return;

    const submissionId = ++this.submissionId;
    const context = {
      productId: this.state.product.id,
      productTitle: this.state.product.title,
      variantId: variant.id,
      quantity: this.state.quantity,
      trigger: this.activeTrigger,
      sessionId: this.sessionId,
      addedLabel: this.state.copy.added,
      previousVariantQuantity: this.getKnownVariantQuantity(variant.id),
    };
    this.state.isSubmitting = true;
    this.clearError();
    this.setStatus('');
    this.updateQuantity();
    this.updateVariantUi();
    const mutationSequence = getNextCartMutationSequence();
    const previousCartCount = this.getCurrentCartCount();
    const controller = new AbortController();
    this.addAbortController = controller;

    try {
      const formData = new FormData();
      formData.set('id', String(context.variantId));
      formData.set('quantity', String(context.quantity));
      this.appendCartSectionsToFormData(formData);

      const { response, payload, baselineCart, reconciledCart, confirmedCart } = await this.submitCartAdd(
        formData,
        controller.signal,
        context
      );
      if (!reconciledCart && (!response?.ok || payload?.status)) {
        const error = new Error(payload?.description || payload?.message || 'Unable to add this item.');
        error.status = Number(payload?.status || response?.status);
        error.baselineCart = baselineCart;
        throw error;
      }
      const baselineItemCount = Number(baselineCart?.item_count);
      const itemCountBeforeAdd = Number.isFinite(baselineItemCount) ? baselineItemCount : previousCartCount;
      const hasUsableDrawerSection = !reconciledCart && this.hasUsableCartDrawerSection(payload);

      if (submissionId === this.submissionId && context.sessionId === this.sessionId) {
        this.state.isSubmitting = false;
        this.state.isSuccess = true;
        this.updateQuantity();
        this.updateVariantUi();
        this.setStatus(`${context.productTitle}: ${this.state.copy.added}`);
      }
      this.markCardAdded(context);

      const cartDrawerAutoOpens = Boolean(document.querySelector('cart-drawer-component[auto-open]'));
      if (cartDrawerAutoOpens && this.root.open) {
        await this.close();
      }

      const latestConfirmedCart = reconciledCart || confirmedCart;
      if (hasUsableDrawerSection) {
        this.clearCartDrawerStale();
      } else {
        this.markCartDrawerStale(latestConfirmedCart || { item_count: itemCountBeforeAdd + context.quantity });
      }

      const didDispatchFastCartAdd = hasUsableDrawerSection && this.dispatchFastCartAdd(payload, {
        itemCountFallback: itemCountBeforeAdd + context.quantity,
        variantId: context.variantId,
        addedQuantity: context.quantity,
        mutationSequence,
        context,
      });
      if (didDispatchFastCartAdd) {
        this.clearCartDrawerStale();
      } else {
        this.markCartDrawerStale(latestConfirmedCart || { item_count: itemCountBeforeAdd + context.quantity });
      }
      const confirmedItemCount = Number(latestConfirmedCart?.item_count ?? itemCountBeforeAdd + context.quantity);
      this.storeLatestCart(latestConfirmedCart || { item_count: confirmedItemCount });
      if (!reconciledCart && (!hasUsableDrawerSection || !didDispatchFastCartAdd)) {
        this.dispatchCartAddEvent({
          cart: { item_count: confirmedItemCount },
          itemCount: confirmedItemCount,
          variantId: context.variantId,
          addedQuantity: context.quantity,
          mutationSequence,
          serialized: true,
          skipSectionRender: true,
          context,
        });
      }
      if (reconciledCart?.items) {
        const didDispatchConfirmedCartAdd = this.dispatchCartAddEvent({
          cart: reconciledCart,
          itemCount: Number(reconciledCart.item_count),
          variantId: context.variantId,
          addedQuantity: context.quantity,
          mutationSequence,
          serialized: true,
          skipSectionRender: didDispatchFastCartAdd,
          skipBubbleAnimation: true,
          context,
        });
        if (didDispatchFastCartAdd || didDispatchConfirmedCartAdd) this.clearCartDrawerStale();
      }

      if (!cartDrawerAutoOpens && context.sessionId === this.sessionId) {
        window.clearTimeout(this.successTimer);
        this.successTimer = window.setTimeout(() => {
          if (context.sessionId === this.sessionId) this.close();
        }, SUCCESS_CLOSE_DELAY_MS);
      }
    } catch (error) {
      let recoveredCart = error?.reconciledCart;
      if (!recoveredCart?.items && Number(error?.status) === 422 && !error?.cartBaselineFailed) {
        recoveredCart = await this.fetchCurrentCart(controller.signal, CART_SNAPSHOT_TIMEOUT_MS).catch(() => null);
      }
      if (recoveredCart?.items) {
        this.storeLatestCart(recoveredCart);
        this.markCartDrawerStale(recoveredCart);
        const baselineVariantQuantity = this.getVariantQuantity(error?.baselineCart, context.variantId);
        const recoveredVariantQuantity = this.getVariantQuantity(recoveredCart, context.variantId);
        const recoveredAddedQuantity = Math.max(0, recoveredVariantQuantity - baselineVariantQuantity);
        if (recoveredAddedQuantity > 0) {
          this.markCardAdded(context);
          this.dispatchCartAddEvent({
            cart: recoveredCart,
            itemCount: Number(recoveredCart.item_count),
            variantId: context.variantId,
            addedQuantity: recoveredAddedQuantity,
            mutationSequence,
            serialized: true,
            skipSectionRender: true,
            skipBubbleAnimation: true,
            context,
          });
        }
      }

      if (submissionId === this.submissionId && context.sessionId === this.sessionId) {
        this.state.isSubmitting = false;
        this.state.isSuccess = false;
        this.updateQuantity();
        this.updateVariantUi();
        this.setError(this.getSafeErrorMessage(error));
      }
    } finally {
      if (this.addAbortController === controller) this.addAbortController = null;
    }
  }

  async reconcileCartAdd(context, baselineCart, signal) {
    const previousVariantQuantity = this.getVariantQuantity(baselineCart, context.variantId);
    const baselineItemCount = Number(baselineCart.item_count);
    const baselineQuantityTotal = (baselineCart.items || []).reduce(
      (total, item) => total + (Number(item.quantity) || 0),
      0
    );
    const hasCompleteBaseline = baselineQuantityTotal === baselineItemCount;
    let unchangedPolls = 0;
    let latestCart = null;

    for (const delayMs of CART_RECONCILE_POLL_DELAYS_MS) {
      await this.delay(delayMs);
      try {
        const cart = await this.fetchCurrentCart(signal, CART_SNAPSHOT_TIMEOUT_MS);
        latestCart = cart;
        const nextVariantQuantity = this.getVariantQuantity(cart, context.variantId);
        if (hasCompleteBaseline && nextVariantQuantity >= previousVariantQuantity + context.quantity) {
          return { state: 'committed', cart };
        }
        if (
          Number(cart.item_count) === baselineItemCount
          && (!hasCompleteBaseline || nextVariantQuantity === previousVariantQuantity)
        ) {
          unchangedPolls += 1;
          if (unchangedPolls >= 2) return { state: 'unchanged', cart };
          continue;
        }
      } catch (_) {
        // Both short polls must positively confirm an unchanged cart before a retry is allowed.
      }
    }

    return unchangedPolls >= 2
      ? { state: 'unchanged', cart: latestCart }
      : { state: 'unknown', cart: latestCart };
  }

  markCardAdded(context) {
    const card = context.trigger?.closest?.('.sh-adore-rec-card');
    if (!(card instanceof HTMLElement)) return;
    card.classList.add('is-selected');
    card.dataset.selectedVariantId = String(context.variantId);
    const buttonLabel = context.trigger.querySelector('[data-sh-card-button-label]');
    if (buttonLabel) buttonLabel.textContent = context.addedLabel;
    const status = card.querySelector('[data-sh-card-status]');
    if (status) status.textContent = `${context.productTitle}: ${context.addedLabel}`;
  }

  onCartUpdate(event) {
    const cart = event.detail?.resource;
    if (!Array.isArray(cart?.items)) return;
    this.syncCardStates(cart);
  }

  syncCardStates(cart) {
    const productIds = new Set(cart.items.map((item) => String(item.product_id || '')));
    document.querySelectorAll('.sh-adore-rec-card[data-complete-product-id]').forEach((card) => {
      const isInCart = productIds.has(String(card.dataset.completeProductId || ''));
      const trigger = card.querySelector('[data-sh-addon-quick-add]');
      const label = trigger?.querySelector('[data-sh-card-button-label]');
      card.classList.toggle('is-selected', isInCart);
      if (label && trigger) {
        const copy = this.getCopy(trigger.closest('[data-sh-addon-quick-add-section]'));
        label.textContent = isInCart ? copy.added : trigger.dataset.defaultLabel || 'QUICK ADD';
      }
      if (!isInCart) {
        card.removeAttribute('data-selected-variant-id');
        const status = card.querySelector('[data-sh-card-status]');
        if (status) status.textContent = '';
      }
    });
  }

  getKnownVariantQuantity(variantId) {
    const cart = window.SecretHourLatestCart;
    return Array.isArray(cart?.items) ? this.getVariantQuantity(cart, variantId) : NaN;
  }

  getVariantQuantity(cart, variantId) {
    return (cart?.items || []).reduce((total, item) => {
      const itemVariantId = String(item.variant_id || item.id || '');
      return itemVariantId === String(variantId) ? total + (Number(item.quantity) || 0) : total;
    }, 0);
  }

  async fetchCurrentCart(signal, timeoutMs = 10000) {
    return fetchAuthoritativeCart({ signal, timeoutMs });
  }

  async fetchCartSnapshotWithRetry(signal) {
    if (signal?.aborted) throw signal.reason || new DOMException('Aborted', 'AbortError');
    const cachedCart = getCachedCart();
    if (cachedCart) return cachedCart;

    const domCart = this.getCartIdentityFromDom();
    if (domCart) return domCart;

    if (this.getCurrentCartCount() === 0) {
      return { item_count: 0, items: [] };
    }

    try {
      return await this.fetchCurrentCart(signal, CART_SNAPSHOT_TIMEOUT_MS);
    } catch (error) {
      if (this.getCurrentCartCount() === 0) return { item_count: 0, items: [] };
      error.cartBaselineFailed = true;
      throw error;
    }
  }

  getCartIdentityFromDom() {
    const cartState = window[CART_STATE_MANAGER_KEY];
    const cart = cartState?.buildOptimisticCartFromDom?.();
    if (!cart || !Array.isArray(cart.items)) return null;
    const quantityTotal = cart.items.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
    const itemCount = Number(cart.item_count);
    return quantityTotal === itemCount && itemCount === this.getCurrentCartCount() ? cart : null;
  }

  createConfirmedCartIdentity(baselineCart, context) {
    if (!Array.isArray(baselineCart?.items)) return null;
    const baselineItemCount = Number(baselineCart.item_count);
    const baselineQuantityTotal = baselineCart.items.reduce(
      (total, item) => total + (Number(item.quantity) || 0),
      0
    );
    if (baselineQuantityTotal !== baselineItemCount) return null;

    const quantities = new Map();
    for (const item of baselineCart.items) {
      const variantId = String(item.variant_id || item.id || '');
      if (!variantId) continue;
      quantities.set(variantId, (quantities.get(variantId) || 0) + (Number(item.quantity) || 0));
    }

    const variantId = String(context.variantId);
    quantities.set(variantId, (quantities.get(variantId) || 0) + Number(context.quantity));
    return {
      __secretHourIdentityOnly: true,
      item_count: baselineItemCount + Number(context.quantity),
      items: Array.from(quantities, ([id, quantity]) => ({
        id: Number(id),
        variant_id: Number(id),
        quantity,
      })),
    };
  }

  markCartDrawerStale(cart) {
    if (!cart || Number(cart.item_count) <= 0) return;
    if (Array.isArray(cart.items)) this.storeLatestCart(cart);
    window.SecretHourCartDrawerNeedsRefresh = true;
    const cartState = window[CART_STATE_MANAGER_KEY];
    if (cartState && typeof cartState === 'object') cartState.drawerNeedsRefresh = true;
  }

  storeLatestCart(cart) {
    storeLatestCart(cart);
  }

  clearCartDrawerStale() {
    window.SecretHourCartDrawerNeedsRefresh = false;
    const cartState = window[CART_STATE_MANAGER_KEY];
    if (cartState && typeof cartState === 'object') cartState.drawerNeedsRefresh = false;
  }

  async submitCartAdd(formData, signal, context) {
    return this.enqueueCartAddRequest(async () => {
      const baselineCart = await this.fetchCartSnapshotWithRetry(signal);
      context.previousVariantQuantity = this.getVariantQuantity(baselineCart, context.variantId);
      return this.fetchCartAddWithRetry(formData, signal, context, baselineCart);
    });
  }

  async enqueueCartAddRequest(task) {
    const cartState = window[CART_STATE_MANAGER_KEY];
    if (cartState?.pendingUpdates?.size > 0 && typeof cartState.flushPendingUpdates === 'function') {
      await cartState.flushPendingUpdates();
    }
    return enqueueCartMutation(task);
  }

  async fetchCartAddWithRetry(formData, signal, context, baselineCart) {
    const maxRetries = 1;
    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
      let response;
      let payload;
      let requestError;
      try {
        const requestBody = attempt === 0 ? formData : this.copyCartAddFormDataWithoutSections(formData);
        response = await this.fetchWithTimeout(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: requestBody,
          signal,
        }, CART_REQUEST_TIMEOUT_MS);
        payload = await this.parseJsonResponse(
          response,
          this.getRequestFallbackMessage(response.status, 'Unable to add this item. Please try again.')
        );
      } catch (error) {
        requestError = error;
      }

      const payloadStatus = Number(payload?.status);
      const requestErrorStatus = Number(requestError?.status);
      const effectiveStatus = Number.isFinite(payloadStatus) && payloadStatus >= 400
        ? payloadStatus
        : Number.isFinite(requestErrorStatus) && requestErrorStatus >= 400
          ? requestErrorStatus
          : response?.status;
      if (!requestError && response?.ok && !(Number.isFinite(payloadStatus) && payloadStatus >= 400)) {
        return {
          response,
          payload,
          baselineCart,
          reconciledCart: null,
          confirmedCart: this.createConfirmedCartIdentity(baselineCart, context),
        };
      }

      const hasExplicitRetryableStatus = CART_ADD_RETRYABLE_STATUS_CODES.has(effectiveStatus);
      const hasUncertainTransportError = Boolean(
        !response
        && requestError
        && (
          requestError?.name === 'AbortError'
          || requestError?.name === 'TimeoutError'
          || requestError?.name === 'TypeError'
          || requestError instanceof TypeError
        )
      );
      const hasSuccessfulInvalidJson = Boolean(response?.ok && requestError);
      const shouldReconcile = hasExplicitRetryableStatus || hasUncertainTransportError || hasSuccessfulInvalidJson;
      if (!shouldReconcile) {
        if (requestError) throw requestError;
        return { response, payload, baselineCart, reconciledCart: null };
      }

      const isDefinitelyRejected = effectiveStatus === 409 || effectiveStatus === 425 || effectiveStatus === 429;
      if (isDefinitelyRejected) {
        const retrySource =
          response?.status >= 400
            ? response
            : requestError || { status: effectiveStatus };
        const retryDelay = noteCartMutationCooldown(retrySource, attempt);
        const canRetryRejected = attempt < maxRetries && !signal?.aborted;
        if (canRetryRejected) {
          await this.delay(retryDelay);
          continue;
        }

        const error = new Error('Your bag could not update just now. Please try again.');
        error.status = effectiveStatus;
        throw error;
      }

      const reconciliation = await this.reconcileCartAdd(context, baselineCart, signal);
      if (reconciliation.state === 'committed') {
        return { response: null, payload: {}, baselineCart, reconciledCart: reconciliation.cart };
      }

      const canRetryConfirmedUnchanged =
        reconciliation.state === 'unchanged'
        && attempt < maxRetries
        && !signal?.aborted;
      if (canRetryConfirmedUnchanged) {
        const retrySource =
          response?.status >= 400
            ? response
            : requestError || { status: effectiveStatus };
        await this.delay(this.getRetryDelay(retrySource, attempt));
        continue;
      }

      const unchangedMessage = effectiveStatus === 429
        ? 'Your bag could not update just now. Please try again.'
        : payload?.description || payload?.message || requestError?.message || 'Unable to add this item. Please try again.';
      const error = new Error(
        reconciliation.state === 'unchanged'
          ? unchangedMessage
          : 'We could not confirm this cart update. Please check your bag before trying again.'
      );
      if (Number.isFinite(Number(effectiveStatus))) error.status = Number(effectiveStatus);
      error.reconciledCart = reconciliation.cart || null;
      throw error;
    }
    throw new Error('Please wait a moment and try again.');
  }

  copyCartAddFormDataWithoutSections(formData) {
    const copy = new FormData();
    for (const [key, value] of formData.entries()) {
      if (key === 'sections' || key === 'sections_url') continue;
      copy.append(key, value);
    }
    return copy;
  }

  getRetryDelay(response, attempt) {
    return getCartRetryDelay(response, attempt);
  }

  getCartSectionIds() {
    const sectionIds = [];
    const addSectionId = (sectionId) => {
      if (sectionId && !sectionIds.includes(sectionId)) sectionIds.push(sectionId);
    };

    if (document.querySelector('cart-items-component[data-drawer]')) addSectionId(CART_DRAWER_SECTION_ID);
    if (this.isCartPage()) {
      document.querySelectorAll('cart-items-component[data-section-id]:not([data-drawer])').forEach((component) => {
        addSectionId(component.dataset.sectionId);
      });
    }
    return sectionIds.slice(0, 3);
  }

  appendCartSectionsToFormData(formData) {
    const sections = this.getCartSectionIds();
    if (!sections.length) return;
    formData.set('sections', sections.join(','));
    formData.set('sections_url', this.getSectionsUrl());
  }

  hasUsableCartDrawerSection(payload) {
    const sectionId = document.querySelector('cart-items-component[data-drawer][data-section-id]')?.dataset.sectionId;
    if (!sectionId || !payload?.sections || typeof payload.sections !== 'object') return false;
    const sectionHtml = payload.sections[sectionId];
    if (typeof sectionHtml !== 'string' || sectionHtml.trim() === '') return false;
    const fragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
    return Boolean(fragment.querySelector('cart-items-component[data-drawer]'));
  }

  getSectionsUrl() {
    const pathname = window.location.pathname || '/';
    return pathname.startsWith('/') ? pathname : `/${pathname}`;
  }

  isCartPage() {
    const currentPath = window.location.pathname.replace(/\/$/, '') || '/';
    const cartPath = new URL(Theme.routes.cart_url, window.location.origin).pathname.replace(/\/$/, '') || '/';
    return currentPath === cartPath;
  }

  dispatchFastCartAdd(payload, data) {
    const sections = payload?.sections;
    if (!sections || typeof sections !== 'object') return false;
    const sectionItemCount = this.getCartItemCountFromSections(sections);
    const fallbackItemCount = Number(data.itemCountFallback);
    const itemCount = Number.isFinite(sectionItemCount)
      ? sectionItemCount
      : Number.isFinite(fallbackItemCount)
        ? fallbackItemCount
        : 0;

    return this.dispatchCartAddEvent({
      cart: { item_count: itemCount },
      itemCount,
      sections,
      variantId: data.variantId,
      addedQuantity: data.addedQuantity,
      mutationSequence: data.mutationSequence,
      serialized: true,
      provisional: true,
      skipBubbleAnimation: true,
      context: data.context,
    });
  }

  dispatchCartAddEvent({
    cart,
    itemCount,
    sections,
    variantId,
    addedQuantity,
    mutationSequence,
    optimistic = false,
    provisional = false,
    serialized = false,
    skipSectionRender = false,
    skipBubbleAnimation = false,
    context,
  }) {
    const target = context?.trigger instanceof HTMLElement && context.trigger.isConnected ? context.trigger : document;
    const event = new CartAddEvent(cart, String(variantId), {
      source: 'sh-addon-quick-add',
      itemCount,
      variantId: String(variantId),
      productId: context?.productId,
      addedQuantity,
      sections,
      mutationSequence,
      optimistic,
      provisional,
      serialized,
      skipSectionRender,
      skipBubbleAnimation,
    });
    target.dispatchEvent(event);
    return event.detail.data?.sectionRenderApplied === true;
  }

  getCartItemCountFromSections(sections) {
    if (!sections || typeof sections !== 'object') return null;
    for (const sectionHtml of Object.values(sections)) {
      if (typeof sectionHtml !== 'string') continue;
      const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const count = this.extractCountFromText(html.querySelector('[ref="cartItemCount"]')?.textContent);
      if (Number.isFinite(count)) return count;
    }
    return null;
  }

  async fetchWithTimeout(url, options, timeoutMs) {
    return fetchCartWithTimeout(url, options, timeoutMs);
  }

  async parseJsonResponse(response, fallbackMessage) {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (_) {
      const isRateLimited = response.status === 429 || /local_rate_limited|verifying your connection/i.test(text);
      const error = new Error(
        isRateLimited
          ? 'Your bag could not update just now. Please try again.'
          : fallbackMessage || 'Unable to complete this request. Please try again.'
      );
      if (isRateLimited) error.status = 429;
      if (isRateLimited) error.retryAfterMs = this.getRetryDelay(response, 0);
      throw error;
    }
  }

  getSafeErrorMessage(error, isProductLoad = false) {
    if (error?.name === 'AbortError') {
      return isProductLoad
        ? 'This item took too long to load. Please try again.'
        : 'The request took too long. Please try again.';
    }
    const message = String(error?.message || '').trim();
    if (!message || /unexpected token|doctype|not valid json/i.test(message)) {
      return isProductLoad ? 'Unable to load this item. Please try again.' : 'Unable to add this item. Please try again.';
    }
    return message;
  }

  getRequestFallbackMessage(status, fallbackMessage) {
    if (status === 429) return 'Please wait a moment and try again.';
    if (status >= 500) return 'Something went wrong. Please try again.';
    return fallbackMessage;
  }

  getCurrentCartCount() {
    const selectors = ['.cart-bubble__text-count', '[data-testid="cart-count-live-region"]', 'cart-icon', '.cart-bubble'];
    for (const selector of selectors) {
      const count = this.extractCountFromText(document.querySelector(selector)?.textContent);
      if (Number.isFinite(count)) return count;
    }

    try {
      const stored = JSON.parse(sessionStorage.getItem('cart-count') || '{}');
      const storedCount = this.extractCountFromText(stored.value);
      if (Number.isFinite(storedCount)) return storedCount;
    } catch (_) {
      // Storage may be unavailable in private browsing contexts.
    }
    return 0;
  }

  extractCountFromText(text) {
    const match = String(text || '').match(/\d+/);
    return match ? Number(match[0]) : NaN;
  }

  delay(ms) {
    return delayCartRequest(ms);
  }

  setError(message) {
    this.error.textContent = message;
    this.setStatus('');
  }

  setStatus(message) {
    this.status.textContent = message;
  }

  setTriggerError(trigger, message) {
    const status = trigger.closest('.sh-adore-rec-card')?.querySelector('[data-sh-card-status]');
    if (!status) return;
    status.textContent = message;
    window.setTimeout(() => {
      if (status.isConnected && status.textContent === message) status.textContent = '';
    }, 3000);
  }

  clearTriggerStatus(trigger) {
    const status = trigger.closest('.sh-adore-rec-card')?.querySelector('[data-sh-card-status]');
    if (status && !trigger.closest('.sh-adore-rec-card')?.classList.contains('is-selected')) status.textContent = '';
  }

  clearError() {
    if (this.error) this.error.textContent = '';
  }

  getFocusableElements() {
    if (!this.drawer) return [];
    return Array.from(
      this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), details summary, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);
  }

  lockScroll() {
    if (this.isScrollLocked) return;
    this.scrollY = window.scrollY || window.pageYOffset || 0;
    this.bodyTopBeforeLock = document.body.style.top;
    this.isScrollLocked = true;
    document.body.classList.add('sh-quick-add-scroll-lock');
    document.body.style.top = `-${this.scrollY}px`;
  }

  unlockScroll() {
    if (!this.isScrollLocked) return;
    const targetY = this.scrollY || 0;
    const documentRoot = document.documentElement;
    const previousRootScrollBehavior = documentRoot.style.scrollBehavior;
    const previousBodyScrollBehavior = document.body.style.scrollBehavior;
    documentRoot.style.scrollBehavior = 'auto';
    document.body.style.scrollBehavior = 'auto';
    document.body.classList.remove('sh-quick-add-scroll-lock');
    document.body.style.top = this.bodyTopBeforeLock;
    window.scrollTo({ left: 0, top: targetY, behavior: 'auto' });
    this.isScrollLocked = false;

    requestAnimationFrame(() => {
      documentRoot.style.scrollBehavior = previousRootScrollBehavior;
      document.body.style.scrollBehavior = previousBodyScrollBehavior;
    });
  }
}

const bootSecretHourAddonQuickAdd = () => {
  if (window.SecretHourAddonQuickAdd) return;
  window.SecretHourAddonQuickAdd = new SecretHourAddonQuickAdd();
  window.SecretHourAddonQuickAdd.init();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootSecretHourAddonQuickAdd, { once: true });
} else {
  bootSecretHourAddonQuickAdd();
}
